import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import type { Product } from 'brokertslib/src/enums'
import { getProductShortName } from 'brokertslib/src/enums'

import type { OrganizationUnitTree } from 'BrokerTsLib/src/containers'
import { baseUrl } from '.'
import type { Rtpc } from '../dtos/rtpc/Rtpc'
import type { RtpcQuery } from '../dtos/rtpc/RtpcQuery'

const rtpcApiUrl = import.meta.env.VITE_RTPC_API
const url = `${baseUrl}/${rtpcApiUrl}`

export const useSearchRtpc = (filter: RtpcQuery) =>
  useQuery({ queryKey: ['rtpcs', filter], queryFn: () => axios.post<Rtpc[]>(`${url}/Search`, filter) })

export const useGetProducts = () =>
  useQuery({ queryKey: ['useGetProducts'], queryFn: () => axios.get<Product[]>(`${url}/GetProducts`) })

export const useGetInsurers = () =>
  useQuery({
    queryKey: ['useGetInsurers'],
    queryFn: () => axios.get<{ insurer: string; insurerId: number }[]>(`${url}/GetInsurers`),
  })

export const useGetInsuranceCategoryInsurers = () => {
  const query = useQuery({
    queryKey: ['useGetInsuranceCategoryInsurers'],
    queryFn: () =>
      axios.get<
        {
          id: number
          insuranceCategoryTypeId: number
          insurer: string
          insurerId: number
          serviceTypeId: number
          serviceType: string
        }[]
      >(`${url}/GetInsuranceCategoryInsurers`),
  })
  return {
    ...query,
    getInsuranceCategoryInsurerText: (insuranceCategoryInsurerId: number) => {
      const ici = query.data?.data.find(
        (x) => x.id === insuranceCategoryInsurerId,
      )
      return `${getProductShortName(ici?.insuranceCategoryTypeId)} - ${ici?.insurer} - ${ici?.serviceType}`
    },
  }
}

export const useDeleteRtpc = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rtpcId: number) => axios.delete(`${url}/Delete/${rtpcId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rtpcs'] })
    },
  })
}

export const useSaveRtpc = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rtpcs: Rtpc[]) =>
      axios.post<Rtpc[]>(`${url}/Save`, rtpcs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rtpcs'] })
    },
  })
}

export const useGetAvailableOus = (rtpcId: number) =>
  useQuery({
    queryKey: ['rtpcAvailableOus', rtpcId],
    queryFn: () => axios.get<OrganizationUnitTree[]>(`${url}/GetAvailableOus`, { params: { rtpcId } }),
  })

export const useGetRtpcDetail = (rtpcId: number) =>
  useQuery({
    queryKey: ['rtpc', rtpcId],
    queryFn: () => axios.get<{ rtpc: Rtpc; ouIds: number[] }>(`${url}/GetRtpcDetail`, { params: { rtpcId } }),
  })

export const useSaveRtpcOus = (rtpcId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (detail: { rtpcId: number; ouIds: number[] }) =>
      axios.post(`${url}/SaveRtpcOus`, detail.ouIds, {
        params: { rtpcId: detail.rtpcId },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rtpc', rtpcId] })
    },
  })
}
